import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleChange, MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, combineLatest, filter, of, switchMap } from 'rxjs';
import { CompanyProductObjectiveComponent } from 'src/app/components/main/employees/dashboard/company-product-objective/company-product-objective.component';
import { CompanyObjectiveKeyResultComponent } from 'src/app/components/main/employees/dashboard/company-product-objective/company-objective-key-result.component';
import { COMPANY_OBJECTIVE_PERIOD_STATUS_LABELS, CompanyObjectivePeriodStatus } from 'src/app/enums/company-objective-period-status.enum';
import { ActiveCompanyObjective, ActiveCompanyObjectiveKeyResult } from 'src/app/models/active-company-objective.model';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { OkrManagementObjective, OkrManagementPeriod, OkrManagementTree } from 'src/app/models/okr-management.model';
import { ApiService } from 'src/app/services/api.service';
import { CompanyProductPageIdentityComponent } from '../company-product-page-identity/company-product-page-identity.component';
import { CompanyObjectiveFormDialogComponent } from './company-objective-form-dialog.component';
import { CompanyObjectiveKeyResultFormDialogComponent } from './company-objective-key-result-form-dialog.component';
import { CompanyObjectivePeriodDialogComponent } from './company-objective-period-dialog.component';
import { CompanyObjectiveViewDialogComponent } from './company-objective-view-dialog.component';

@Component({
  selector: 'app-company-product-objectives',
  templateUrl: './company-product-objectives.component.html',
  styleUrl: './company-product-objectives.component.css',
  imports: [
    CompanyProductPageIdentityComponent,
    CompanyProductObjectiveComponent,
    CompanyObjectiveKeyResultComponent,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyProductObjectivesComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api = inject(ApiService);
  private readonly activated_route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly destroy_ref = inject(DestroyRef);

  protected readonly CompanyObjectivePeriodStatus = CompanyObjectivePeriodStatus;

  readonly company_product_id = Number(this.activated_route.parent?.snapshot.paramMap.get('id'));
  readonly period_id = signal<number | null>(null);
  readonly view = signal<'company' | 'product'>('product');
  readonly refresh_tick = signal(0);
  readonly expanded = signal<'none' | 'company' | number>('none');
  private default_view_applied = false;
  private last_applied_view: 'company' | 'product' | null = null;

  readonly company_product = toSignal(
    this.api.getCompanyProduct(String(this.company_product_id), { with: 'company.company_products' }).pipe(catchError(() => of(null))),
    { initialValue: null }
  );

  readonly tree = toSignal(
    combineLatest([toObservable(this.period_id), toObservable(this.view), toObservable(this.refresh_tick)]).pipe(
      switchMap(([period_id, view]) => this.api.getOkrManagement(this.company_product_id, period_id, view).pipe(catchError(() => of(null))))
    )
  );

  readonly page = computed(() => buildOkrManagementPage(this.tree(), this.view(), this.expanded(), this.company_product_id));
  readonly can_toggle_view = computed(() => this.tree()?.permissions?.can_toggle_view === true);
  readonly company_logo_url = computed((): string => this.company_product()?.logo_url || '/img/default-company_product.png');
  readonly show_product_objective_plus = computed(() => {
    const page = this.page();
    if (page.state !== 'ready' || !page.can_create_product_objective) {
      return false;
    }
    if (page.view === 'product') {
      return (page.product_objectives?.length ?? 0) === 0;
    }

    return this.availableProductsWithoutObjective().length > 0;
  });

  constructor() {
    this.lockContentContainerScroll();

    this.activated_route.queryParamMap.pipe(takeUntilDestroyed()).subscribe(params => {
      const period = params.get('period');
      const view = params.get('view');
      if (period) {
        this.period_id.set(Number(period));
      }
      if (view === 'company' || view === 'product') {
        this.view.set(this.can_toggle_view() ? view : 'product');
      } else if (!this.can_toggle_view()) {
        this.view.set('product');
      }
    });

    effect(() => {
      const tree = this.tree();
      if (!tree?.permissions || this.default_view_applied) {
        return;
      }

      this.default_view_applied = true;
      const query_view = this.activated_route.snapshot.queryParamMap.get('view');
      if (query_view === 'company' || query_view === 'product') {
        this.view.set(tree.permissions.can_toggle_view ? query_view : 'product');
        return;
      }

      this.view.set(tree.permissions.can_toggle_view ? 'company' : 'product');
    });

    effect(() => {
      const tree = this.tree();
      if (!tree?.period) {
        return;
      }

      const query = this.activated_route.snapshot.queryParamMap;
      if (query.get('period') === String(tree.period.id) && query.get('view') === this.view()) {
        return;
      }

      void this.router.navigate([], {
        relativeTo: this.activated_route,
        queryParams: { period: tree.period.id, view: this.view() },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });

    effect(() => {
      const view = this.view();
      const tree = this.tree();
      if (!tree) {
        return;
      }

      const should_expand_route_product = view === 'product' && this.last_applied_view !== 'product';
      this.last_applied_view = view;
      if (should_expand_route_product) {
        this.expandRouteProduct();
      }
    });
  }

  setView(change: MatButtonToggleChange): void {
    const view = change.value === 'company' ? 'company' : 'product';
    this.expanded.set(view === 'company' ? 'none' : this.expanded());
    this.view.set(view);
    void this.router.navigate([], {
      relativeTo: this.activated_route,
      queryParams: { view, period: this.period_id() },
      queryParamsHandling: 'merge',
    });
  }

  goToOkrAndMetrics(): void {
    void this.router.navigate(['..'], { relativeTo: this.activated_route });
  }

  goToBacklog(): void {}

  goToPreviousPeriod(): void {
    const previous_period_id = this.tree()?.period?.previous_period_id;
    if (previous_period_id) {
      this.goToPeriod(previous_period_id);
    }
  }

  goToNextPeriod(): void {
    const next_period_id = this.tree()?.period?.next_period_id;
    if (next_period_id) {
      this.goToPeriod(next_period_id);
    }
  }

  toggleCompanyCard(): void {
    if (!this.tree()?.company_objective) {
      return;
    }

    if (this.expanded() === 'company') {
      if (this.view() === 'product') {
        this.expandRouteProduct();
        return;
      }

      this.expanded.set('none');
      return;
    }

    this.expanded.set('company');
  }

  toggleProductCard(objective_id: number): void {
    this.expanded.update(expanded => (expanded === objective_id ? 'none' : objective_id));
  }

  openCreatePeriodDialog(): void {
    this.dialog
      .open(CompanyObjectivePeriodDialogComponent, {
        data: {
          mode: 'create',
          existing_periods: this.tree()?.company_periods ?? [],
        },
      })
      .afterClosed()
      .pipe(
        filter((result): result is { start_date: string; end_date: string } => result != null),
        switchMap(result => {
          const company_id = this.company_product()?.company_id;
          if (company_id == null) {
            return of(null);
          }

          return this.api.storeCompanyObjectivePeriod({
            company_id,
            start_date: result.start_date,
            end_date: result.end_date,
          });
        }),
        filter((period): period is { id: number } => period != null),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe({
        next: period => {
          this.goToPeriod(period.id);
          this.refresh_tick.update(tick => tick + 1);
        },
      });
  }

  openEditPeriodDialog(): void {
    const period = this.tree()?.period;
    if (!period || period.status !== CompanyObjectivePeriodStatus.Future) {
      return;
    }

    this.dialog
      .open(CompanyObjectivePeriodDialogComponent, {
        data: {
          mode: 'edit',
          start_date: period.start_date,
          end_date: period.end_date,
          existing_periods: (this.tree()?.company_periods ?? []).filter(company_period => company_period.id !== period.id),
        },
      })
      .afterClosed()
      .pipe(
        filter((result): result is { start_date: string; end_date: string } => result != null),
        switchMap(result => this.api.updateCompanyObjectivePeriod(period.id, result)),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe({
        next: () => this.refresh_tick.update(tick => tick + 1),
      });
  }

  deletePeriod(): void {
    const period = this.tree()?.period;
    if (!period || period.status !== CompanyObjectivePeriodStatus.Future) {
      return;
    }

    if (!confirm('Se eliminarán el periodo y todos sus objetivos y Key Results. ¿Continuar?')) {
      return;
    }

    this.api
      .destroyCompanyObjectivePeriod(period.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: () => {
          this.period_id.set(null);
          this.refresh_tick.update(tick => tick + 1);
        },
      });
  }

  openCreateCompanyObjective(): void {
    const tree = this.tree();
    const company_id = this.company_product()?.company_id;
    if (!tree?.period || !company_id) {
      return;
    }

    this.openObjectiveDialog({
      title: undefined,
      relatable_type: 'company',
      mode: 'create',
      relatable_id: company_id,
    });
  }

  openCreateProductObjective(): void {
    const available_products = this.availableProductsWithoutObjective();
    if (available_products.length === 0) {
      return;
    }

    this.openObjectiveDialog({
      title: undefined,
      relatable_type: 'company_product',
      mode: 'create',
      available_products,
      relatable_id: available_products[0].id,
    });
  }

  openEditObjective(objective: OkrManagementObjective): void {
    this.openObjectiveDialog({
      title: objective.title,
      relatable_type: isCompanyObjective(objective) ? 'company' : 'company_product',
      mode: 'edit',
      objective_id: objective.id,
    });
  }

  openViewObjective(objective: OkrManagementObjective): void {
    const period = this.tree()?.period;
    if (!period) {
      return;
    }

    this.dialog.open(CompanyObjectiveViewDialogComponent, {
      data: {
        objective: toActiveCompanyObjective(objective, period),
      },
      width: '800px',
    });
  }

  deleteObjective(objective: OkrManagementObjective): void {
    if (!confirm('¿Eliminar este objetivo?')) {
      return;
    }

    this.api
      .destroyCompanyObjective(objective.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: () => this.refresh_tick.update(tick => tick + 1),
      });
  }

  openCreateKeyResult(objective: OkrManagementObjective): void {
    this.openKeyResultDialog(objective, {
      mode: 'create',
    });
  }

  openEditKeyResult(objective: OkrManagementObjective, key_result: ActiveCompanyObjectiveKeyResult): void {
    this.openKeyResultDialog(objective, {
      mode: 'edit',
      order: key_result.order,
      key_result,
    });
  }

  deleteKeyResult(key_result: ActiveCompanyObjectiveKeyResult): void {
    if (!confirm('¿Eliminar este Key Result?')) {
      return;
    }

    this.api
      .destroyCompanyObjectiveKeyResult(key_result.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: () => this.refresh_tick.update(tick => tick + 1),
      });
  }

  onVariableConditionUpdated(): void {
    this.refresh_tick.update(tick => tick + 1);
  }

  private expandRouteProduct(): void {
    const tree = this.tree();
    const mine =
      tree?.product_objectives.find(objective => objective.relatable_id === this.company_product_id) ?? tree?.product_objectives[0];
    this.expanded.set(mine ? mine.id : 'none');
  }

  private lockContentContainerScroll(): void {
    const content_container = document.getElementById('content-container');
    if (content_container === null) {
      return;
    }

    const previous_overflow_y = content_container.style.overflowY;
    content_container.style.overflowY = 'hidden';
    this.destroy_ref.onDestroy(() => {
      content_container.style.overflowY = previous_overflow_y;
    });
  }

  private openObjectiveDialog(options: {
    title?: string;
    relatable_type: 'company' | 'company_product';
    mode: 'create' | 'edit';
    available_products?: { id: number; title: string }[];
    relatable_id?: number;
    objective_id?: number;
  }): void {
    const period = this.tree()?.period;
    if (!period) {
      return;
    }

    this.dialog
      .open(CompanyObjectiveFormDialogComponent, {
        width: '640px',
        maxWidth: '95vw',
        data: {
          company_product_id: this.company_product_id,
          company_objective_period_id: period.id,
          company_id: this.company_product()?.company_id ?? 0,
          objective_id: options.objective_id,
          title: options.title,
          relatable_type: options.relatable_type,
          mode: options.mode,
          available_products: options.available_products,
          relatable_id: options.relatable_id,
        },
      })
      .afterClosed()
      .pipe(
        filter((saved): saved is true => saved === true),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe({
        next: () => this.refresh_tick.update(tick => tick + 1),
      });
  }

  private openKeyResultDialog(
    objective: OkrManagementObjective,
    options: { mode: 'create' | 'edit'; order?: number; key_result?: ActiveCompanyObjectiveKeyResult }
  ): void {
    this.dialog
      .open(CompanyObjectiveKeyResultFormDialogComponent, {
        width: '720px',
        maxWidth: '95vw',
        data: {
          company_product_id: isCompanyObjective(objective) ? this.company_product_id : objective.relatable_id,
          company_objective_id: objective.id,
          key_result_id: options.key_result?.id,
          objective_title: objective.title,
          level_label: isCompanyObjective(objective) ? 'Company Objective' : 'Producto: ' + (objective.company_product_name ?? ''),
          next_order: options.order,
          relatable: isCompanyObjective(objective) ? 'company' : 'product',
          title: options.key_result?.title,
          category: options.key_result?.category,
          evaluation: options.key_result?.evaluation ?? undefined,
          unit: options.key_result?.unit ?? undefined,
          acceptable_target_value: options.key_result?.acceptable_value ?? undefined,
          good_target_value: options.key_result?.good_value ?? undefined,
          mode: options.mode,
        },
      })
      .afterClosed()
      .pipe(
        filter((saved): saved is true => saved === true),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe({
        next: () => this.refresh_tick.update(tick => tick + 1),
      });
  }

  private availableProductsWithoutObjective(): { id: number; title: string }[] {
    const tree = this.tree();
    const products = this.company_product()?.company?.company_products ?? [];
    const used_ids = new Set((tree?.product_objectives ?? []).map(objective => objective.relatable_id));
    const available = products.filter(product => !used_ids.has(product.id)).map(product => ({ id: product.id, title: product.title }));

    if (available.length > 0) {
      return available;
    }

    if (!used_ids.has(this.company_product_id)) {
      return [{ id: this.company_product_id, title: this.company_product()?.title ?? 'Producto' }];
    }

    return [];
  }

  private goToPeriod(period_id: number): void {
    this.period_id.set(period_id);
    void this.router.navigate([], {
      relativeTo: this.activated_route,
      queryParams: { period: period_id, view: this.view() },
      queryParamsHandling: 'merge',
    });
  }
}

function buildOkrManagementPage(
  tree: OkrManagementTree | null | undefined,
  view: 'company' | 'product',
  expanded: 'none' | 'company' | number,
  route_company_product_id: number
): OkrManagementPage {
  if (tree === undefined) {
    return { state: 'loading' };
  }
  if (tree === null) {
    return { state: 'error' };
  }
  if (tree.period === null) {
    return {
      state: 'empty_periods',
      company_name: tree.company_name,
      can_manage_periods: tree.permissions.can_manage_periods,
    };
  }

  const period = tree.period;
  const company_objective = tree.company_objective;
  const show_product_branch = expanded !== 'company';
  const expanded_product =
    typeof expanded === 'number' ? (tree.product_objectives.find(objective => objective.id === expanded) ?? null) : null;
  const company_key_results = [...(company_objective?.key_results ?? [])].sort((left, right) => left.order - right.order);
  const product_key_results = [...(expanded_product?.key_results ?? [])].sort((left, right) => left.order - right.order);
  const can_create_product_objective =
    (view === 'product' && tree.permissions.can_manage_route_product_objective && tree.product_objectives.length === 0) ||
    (view === 'company' && (tree.permissions.can_manage_company_objective || tree.permissions.can_manage_route_product_objective));

  const company_objective_card = company_objective ? toActiveCompanyObjective(company_objective, period) : null;
  const product_objective_cards = tree.product_objectives.map(objective => ({
    objective,
    card: toActiveCompanyObjective(objective, period),
    warnings: objectiveWarningLabels(objective),
  }));

  return {
    state: 'ready',
    company_name: tree.company_name,
    period,
    permissions: tree.permissions,
    view,
    expanded,
    show_product_branch,
    company_objective,
    company_objective_card,
    company_objective_warnings: company_objective ? objectiveWarningLabels(company_objective) : [],
    product_objective_cards,
    product_objectives: tree.product_objectives,
    expanded_product,
    company_key_results,
    product_key_results,
    can_create_company_objective: tree.permissions.can_manage_company_objective && company_objective === null,
    can_create_product_objective: can_create_product_objective && (view === 'product' ? tree.product_objectives.length === 0 : true),
    can_create_company_key_result: Boolean(company_objective?.can_create_key_result && company_key_results.length < 3),
    can_create_product_key_result: Boolean(expanded_product?.can_create_key_result && product_key_results.length < 3),
    period_progress_pct: period.timeline_progress === null ? 0 : period.timeline_progress * 100,
    period_status_label: COMPANY_OBJECTIVE_PERIOD_STATUS_LABELS[period.status],
    period_date_range_label: periodDateRangeLabelIfCompactTitle(period),
    route_company_product_id,
  };
}

function objectiveWarningLabels(objective: OkrManagementObjective): string[] {
  return objective.warnings.map(warning => {
    if (warning === 'missing_key_results') {
      return objective.key_results.length === 0 ? 'Este objetivo aún no tiene Key Results.' : 'Hay menos de 3 Key Results.';
    }
    if (warning === 'missing_categories') {
      const missing = objective.missing_categories.join(', ');
      return missing === '' ? 'Faltan categorías usage, revenue o satisfaction.' : `Faltan categorías: ${missing}`;
    }

    return warning;
  });
}

function isCompanyObjective(objective: OkrManagementObjective): boolean {
  return !objective.relatable_type.includes('CompanyProduct');
}

export function toActiveCompanyObjective(objective: OkrManagementObjective, period: OkrManagementPeriod): ActiveCompanyObjective {
  return {
    id: objective.id,
    title: objective.title,
    period_title: period.title,
    start_date: period.start_date,
    end_date: period.end_date,
    parent_company_objective_id: objective.parent_company_objective_id,
    company_objective_key_results: objective.key_results,
    company_product_id: isCompanyObjective(objective) ? null : objective.relatable_id,
    company_product_name: objective.company_product_name,
    company_product_logo_url: null,
  };
}

interface OkrManagementPage {
  state: 'loading' | 'error' | 'empty_periods' | 'ready';
  company_name?: string;
  can_manage_periods?: boolean;
  period?: OkrManagementPeriod;
  permissions?: OkrManagementTree['permissions'];
  view?: 'company' | 'product';
  expanded?: 'none' | 'company' | number;
  show_product_branch?: boolean;
  company_objective?: OkrManagementObjective | null;
  company_objective_card?: ActiveCompanyObjective | null;
  company_objective_warnings?: string[];
  product_objective_cards?: { objective: OkrManagementObjective; card: ActiveCompanyObjective; warnings: string[] }[];
  product_objectives?: OkrManagementObjective[];
  expanded_product?: OkrManagementObjective | null;
  company_key_results?: ActiveCompanyObjectiveKeyResult[];
  product_key_results?: ActiveCompanyObjectiveKeyResult[];
  can_create_company_objective?: boolean;
  can_create_product_objective?: boolean;
  can_create_company_key_result?: boolean;
  can_create_product_key_result?: boolean;
  period_progress_pct?: number;
  period_status_label?: string;
  period_date_range_label?: string | null;
  route_company_product_id?: number;
}

const SPANISH_SHORT_MONTH_BY_NUMBER: Record<number, string> = {
  1: 'ene.',
  2: 'feb.',
  3: 'mar.',
  4: 'abr.',
  5: 'may.',
  6: 'jun.',
  7: 'jul.',
  8: 'ago.',
  9: 'sep.',
  10: 'oct.',
  11: 'nov.',
  12: 'dic.',
};

function periodDateRangeLabelIfCompactTitle(period: OkrManagementPeriod): string | null {
  if (!isCompactPeriodTitle(period.title)) {
    return null;
  }

  return `${formatSpanishShortDate(period.start_date)} - ${formatSpanishShortDate(period.end_date)}`;
}

function isCompactPeriodTitle(title: string): boolean {
  return /^Annual \d{4}$/.test(title) || /^Q[1-4] \d{4}$/.test(title);
}

function formatSpanishShortDate(value: string): string {
  const date_only = value.slice(0, 10);
  const [year, month, day] = date_only.split('-').map(part => Number(part));
  const month_label = SPANISH_SHORT_MONTH_BY_NUMBER[month] ?? '';

  return `${String(day).padStart(2, '0')} ${month_label} ${year}`;
}
